import React, { useState, useRef, KeyboardEvent, SyntheticEvent } from 'react'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import Paper from '@mui/material/Paper'
import Popper from '@mui/material/Popper'
import MenuItem from '@mui/material/MenuItem'
import MenuList from '@mui/material/MenuList'
import Stack from '@mui/material/Stack'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { IconButton } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { useAppSelector } from 'redux/Hook'
import { MoreVertOptions } from 'components/Shared/MoreVertActions/utility/Options'
import { prepareFilters } from 'api/ApiActions/Reporting/Overview/PrepareFilters'
import { trackActions } from 'containers/Helper'
import { cardName, cardType } from 'components/Shared/Helpers/SegmentHelper'
import styles from 'components/Shared/MoreVertActions/Morevert.module.scss'

type Props = {
  moreVertOptions: Array<string>
  reference?: React.RefObject<HTMLDivElement>
  fileNameKey: string
}

const MoreVertActions = (props: Props) => {
  const { t } = useTranslation()
  const selectedFilters = useAppSelector(
    (state) => state.filterBuilder.selectedFilters,
  )
  const currentFeatures = useAppSelector((state) => state.features.features)

  const { moreVertOptions, reference, fileNameKey } = props

  const [open, setOpen] = useState(false)
  const anchorRef: any = useRef<HTMLButtonElement>(null)
  const mappedVerticalOptions = MoreVertOptions(t)

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen)
  }

  const handleClose = (event: Event | SyntheticEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return
    }

    setOpen(false)
  }

  const handleListKeyDown = (event: KeyboardEvent) => {
    // handle keyboard events on dropdown
    if (event.key === 'Tab' || event.key === 'Escape') {
      event.preventDefault()
      setOpen(false)
    }
  }

  const handleOptionClick = (option: string) => {
    const widgetName = `${cardName[fileNameKey]}`
    const widgetType = `${cardType[fileNameKey]}`

    trackActions('Chart Downloaded', {
      featureName: 'Overview Dashboard',
      sectionName: `Widget`,
      widgetName,
      widgetType,
      downloadType: option,
      ...currentFeatures?.['segment_analytics_admin']?.enabled,
    })

    if (option === 'csv')
      return mappedVerticalOptions[option].action(
        prepareFilters(selectedFilters),
        fileNameKey,
      )

    if (option === 'table_pdf')
      return mappedVerticalOptions[option].action(fileNameKey)

    return mappedVerticalOptions[option].action(option, fileNameKey, reference)
  }

  return (
    <Stack
      direction="row"
      spacing={2}
      sx={{ float: 'right', margin: '0.5rem 0.5rem 0 0' }}
    >
      <IconButton
        ref={anchorRef}
        id="morevert-button"
        aria-controls={open ? 'morevert-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
        data-html2canvas-ignore="true"
        size="small"
        sx={{ top: '-1.25rem', right: '-1rem', color: 'black' }}
      >
        <MoreVertIcon
          fontSize="small"
          sx={{ float: 'right', opacity: '0.5' }}
        />
      </IconButton>
      <Popper
        open={open}
        placement="bottom-end"
        className={styles.dropdown}
        anchorEl={anchorRef.current}
      >
        <Paper>
          <ClickAwayListener onClickAway={handleClose}>
            <MenuList
              autoFocusItem={open}
              id="morevert-menu"
              aria-labelledby="morevert-button"
              onKeyDown={handleListKeyDown}
            >
              {moreVertOptions.map((option: string, index: number) => {
                return (
                  <MenuItem
                    id="more-vert-icon"
                    key={index}
                    file-type={option}
                    className={styles.menuItem}
                    onClick={() => handleOptionClick(option)}
                  >
                    {mappedVerticalOptions[option].label}
                  </MenuItem>
                )
              })}
            </MenuList>
          </ClickAwayListener>
        </Paper>
      </Popper>
    </Stack>
  )
}

export default MoreVertActions
